import { useState, useEffect } from 'react';
import { Search, Plus, Save, RotateCcw, Trash2, AlertCircle, ArrowUpDown, Edit2, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { getMasterPricing, updateMasterPricing, resetMasterPricing, getCustomers, createCustomer, getCustomer, updateCustomerPricing, deleteCustomer } from '@/lib/api';

const AdminPricing = () => {
  // Master Pricing State
  const [masterPricing, setMasterPricing] = useState([]);
  const [proxyProviders, setProxyProviders] = useState([]);
  const [masterSearch, setMasterSearch] = useState('');
  const [masterSort, setMasterSort] = useState({ field: 'provider', dir: 'asc' });
  const [isLoadingMaster, setIsLoadingMaster] = useState(true);
  
  // Master Pricing Edit State
  const [masterEdits, setMasterEdits] = useState({});
  const [isSavingMaster, setIsSavingMaster] = useState(false);
  const [hasMasterChanges, setHasMasterChanges] = useState(false);

  // Customer Pricing State
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Bulk Adjustment State
  const [adjustmentType, setAdjustmentType] = useState('flat');
  const [adjustmentValue, setAdjustmentValue] = useState(0);
  const [proxyAdjustmentType, setProxyAdjustmentType] = useState('flat');
  const [proxyAdjustmentValue, setProxyAdjustmentValue] = useState(0);
  const [rounding, setRounding] = useState('none');
  const [overrides, setOverrides] = useState({});
  const [proxyOverrides, setProxyOverrides] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Create Customer Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Delete Confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchMasterPricing();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerDetails(selectedCustomerId);
    } else {
      setSelectedCustomer(null);
      resetAdjustments();
    }
  }, [selectedCustomerId]);

  const fetchMasterPricing = async () => {
    setIsLoadingMaster(true);
    try {
      const response = await getMasterPricing();
      setMasterPricing(response.data.pricing);
      setProxyProviders(response.data.proxy_providers || []);
      setMasterEdits({});
      setHasMasterChanges(false);
    } catch (error) {
      toast.error('Failed to load master pricing');
    } finally {
      setIsLoadingMaster(false);
    }
  };

  const handleMasterPriceEdit = (provider, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    setMasterEdits(prev => ({
      ...prev,
      [provider]: numValue
    }));
    setHasMasterChanges(true);
  };

  const handleSaveMasterPricing = async () => {
    const changedPrices = Object.entries(masterEdits)
      .filter(([provider, value]) => {
        const original = masterPricing.find(p => p.provider === provider);
        return value !== '' && value !== original?.fee;
      })
      .map(([provider, fee]) => ({ provider, fee }));

    if (changedPrices.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setIsSavingMaster(true);
    try {
      await updateMasterPricing(changedPrices);
      toast.success(`Updated ${changedPrices.length} provider prices`);
      await fetchMasterPricing();
    } catch (error) {
      toast.error('Failed to save master pricing');
    } finally {
      setIsSavingMaster(false);
    }
  };

  const handleResetMasterPricing = async () => {
    if (!window.confirm('Are you sure you want to reset all prices to defaults? This cannot be undone.')) {
      return;
    }
    
    try {
      await resetMasterPricing();
      toast.success('All prices reset to defaults');
      await fetchMasterPricing();
    } catch (error) {
      toast.error('Failed to reset pricing');
    }
  };

  const getMasterEditValue = (provider, currentFee) => {
    if (masterEdits[provider] !== undefined) {
      return masterEdits[provider];
    }
    return currentFee;
  };

  const hasProviderChanged = (provider, currentFee, defaultFee) => {
    const editValue = masterEdits[provider];
    if (editValue !== undefined && editValue !== '') {
      return editValue !== defaultFee;
    }
    return currentFee !== defaultFee;
  };

  const fetchCustomers = async () => {
    setIsLoadingCustomers(true);
    try {
      const response = await getCustomers();
      setCustomers(response.data.customers);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await getCustomer(customerId);
      setSelectedCustomer(response.data);
      setAdjustmentType(response.data.adjustment_type || 'flat');
      setAdjustmentValue(response.data.adjustment_value || 0);
      setProxyAdjustmentType(response.data.proxy_adjustment_type || 'flat');
      setProxyAdjustmentValue(response.data.proxy_adjustment_value || 0);
      setRounding(response.data.rounding || 'none');
      setOverrides(response.data.overrides || {});
      setProxyOverrides(response.data.proxy_overrides || {});
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const resetAdjustments = () => {
    setAdjustmentType('flat');
    setAdjustmentValue(0);
    setProxyAdjustmentType('flat');
    setProxyAdjustmentValue(0);
    setRounding('none');
    setOverrides({});
    setProxyOverrides({});
    setHasChanges(false);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast.error('Please enter a customer name');
      return;
    }

    setIsCreating(true);
    try {
      const response = await createCustomer(newCustomerName, newCustomerEmail);
      setCustomers([...customers, response.data.customer]);
      setSelectedCustomerId(response.data.customer.id);
      setShowCreateDialog(false);
      setNewCustomerName('');
      setNewCustomerEmail('');
      toast.success('Customer profile created');
    } catch (error) {
      toast.error('Failed to create customer');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomerId) return;

    try {
      await deleteCustomer(selectedCustomerId);
      setCustomers(customers.filter(c => c.id !== selectedCustomerId));
      setSelectedCustomerId('');
      setShowDeleteDialog(false);
      toast.success('Customer profile deleted');
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const handleSaveCustomerPricing = async () => {
    if (!selectedCustomerId) return;

    setIsSaving(true);
    try {
      await updateCustomerPricing(selectedCustomerId, {
        adjustment_type: adjustmentType,
        adjustment_value: adjustmentValue,
        proxy_adjustment_type: proxyAdjustmentType,
        proxy_adjustment_value: proxyAdjustmentValue,
        rounding: rounding,
        overrides: overrides,
        proxy_overrides: proxyOverrides
      });
      setHasChanges(false);
      toast.success('Customer pricing saved');
    } catch (error) {
      toast.error('Failed to save pricing');
    } finally {
      setIsSaving(false);
    }
  };

  const calculateAdjustedFee = (baseFee, isProxy = false, effectiveDirectFee = null) => {
    let adjusted;
    const type = isProxy ? proxyAdjustmentType : adjustmentType;
    const value = isProxy ? proxyAdjustmentValue : adjustmentValue;
    
    // For proxy, use the effective direct fee as the base
    const baseValue = isProxy && effectiveDirectFee !== null ? effectiveDirectFee : baseFee;
    
    if (type === 'flat') {
      // Flat: add/subtract percentage points directly (e.g., +8 on 12% = 20%)
      adjusted = baseValue + value;
    } else {
      // Percent: calculate percentage of base fee and add it (e.g., +10% on 12% = 12 + 1.2 = 13.2%)
      adjusted = baseValue + (baseValue * value / 100);
    }

    // Apply bounds
    adjusted = Math.max(0, Math.min(100, adjusted));

    // Apply rounding
    if (rounding === '1') {
      adjusted = Math.round(adjusted * 10) / 10;
    } else if (rounding === '2') {
      adjusted = Math.round(adjusted * 100) / 100;
    }

    return adjusted;
  };

  const getEffectiveFee = (provider, baseFee, isProxy = false, effectiveDirectFee = null) => {
    const overrideMap = isProxy ? proxyOverrides : overrides;
    if (overrideMap[provider] !== undefined && overrideMap[provider] !== '') {
      return parseFloat(overrideMap[provider]);
    }
    return calculateAdjustedFee(baseFee, isProxy, effectiveDirectFee);
  };

  const handleOverrideChange = (provider, value, isProxy = false) => {
    if (isProxy) {
      const newOverrides = { ...proxyOverrides };
      if (value === '' || value === null) {
        delete newOverrides[provider];
      } else {
        newOverrides[provider] = value;
      }
      setProxyOverrides(newOverrides);
    } else {
      const newOverrides = { ...overrides };
      if (value === '' || value === null) {
        delete newOverrides[provider];
      } else {
        newOverrides[provider] = value;
      }
      setOverrides(newOverrides);
    }
    setHasChanges(true);
  };

  const handleAdjustmentChange = (field, value, isProxy = false) => {
    if (isProxy) {
      if (field === 'type') setProxyAdjustmentType(value);
      if (field === 'value') setProxyAdjustmentValue(parseFloat(value) || 0);
    } else {
      if (field === 'type') setAdjustmentType(value);
      if (field === 'value') setAdjustmentValue(parseFloat(value) || 0);
    }
    if (field === 'rounding') setRounding(value);
    setHasChanges(true);
  };

  // Sorting for master pricing
  const sortedMasterPricing = [...masterPricing]
    .filter(p => p.provider.toLowerCase().includes(masterSearch.toLowerCase()))
    .sort((a, b) => {
      const aVal = masterSort.field === 'provider' ? a.provider : a.fee;
      const bVal = masterSort.field === 'provider' ? b.provider : b.fee;
      if (masterSort.dir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const toggleMasterSort = (field) => {
    if (masterSort.field === field) {
      setMasterSort({ field, dir: masterSort.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setMasterSort({ field, dir: 'asc' });
    }
  };

  // Filtered customer pricing
  const filteredCustomerPricing = masterPricing.filter(p =>
    p.provider.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Count changes preview
  const changesCount = masterPricing.filter(p => {
    const adjusted = calculateAdjustedFee(p.fee);
    return Math.abs(adjusted - p.fee) > 0.001;
  }).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground">Pricing Management</h1>
          <p className="text-muted-foreground text-sm">
            Manage master pricing and customer-specific adjustments
          </p>
        </div>

        <Tabs defaultValue="master" className="space-y-6">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="master" data-testid="tab-master">Master Pricing</TabsTrigger>
            <TabsTrigger value="customer" data-testid="tab-customer">Customer Pricing</TabsTrigger>
          </TabsList>

          {/* Master Pricing Tab */}
          <TabsContent value="master" className="space-y-4">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base font-medium">Master Pricing List</CardTitle>
                <CardDescription>
                  Set base pricing for all providers. Changes here affect all customers unless they have overrides.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search provider..."
                      value={masterSearch}
                      onChange={(e) => setMasterSearch(e.target.value)}
                      className="pl-10 bg-background/50"
                      data-testid="master-search"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Total: {masterPricing.length} providers
                  </p>
                  <div className="flex gap-2 ml-auto">
                    <Button
                      variant="outline"
                      onClick={handleResetMasterPricing}
                      className="gap-2"
                      data-testid="reset-master-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset All to Defaults
                    </Button>
                    <Button
                      onClick={handleSaveMasterPricing}
                      disabled={!hasMasterChanges || isSavingMaster}
                      className="gap-2"
                      data-testid="save-master-btn"
                    >
                      {isSavingMaster ? (
                        <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>

                {hasMasterChanges && (
                  <Alert className="border-primary/50 bg-primary/10">
                    <Edit2 className="h-4 w-4 text-primary" />
                    <AlertDescription className="text-sm">
                      You have unsaved changes. Click "Save Changes" to apply them.
                    </AlertDescription>
                  </Alert>
                )}

                {isLoadingMaster ? (
                  <div className="flex items-center justify-center py-12">
                    <span className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 500px)', minHeight: '400px' }}>
                      <Table data-testid="master-pricing-table">
                        <TableHeader className="sticky top-0 bg-card z-10">
                          <TableRow className="border-border hover:bg-transparent">
                            <TableHead 
                              className="font-semibold text-foreground cursor-pointer hover:text-primary"
                              onClick={() => toggleMasterSort('provider')}
                            >
                              <div className="flex items-center gap-2">
                                Provider Name
                                <ArrowUpDown className="w-4 h-4" />
                              </div>
                            </TableHead>
                            <TableHead className="font-semibold text-foreground text-right w-24">
                              Default (%)
                            </TableHead>
                            <TableHead 
                              className="font-semibold text-foreground text-right cursor-pointer hover:text-primary w-32"
                              onClick={() => toggleMasterSort('fee')}
                            >
                              <div className="flex items-center justify-end gap-2">
                                Base Fee (%)
                                <ArrowUpDown className="w-4 h-4" />
                              </div>
                            </TableHead>
                            <TableHead className="font-semibold text-foreground text-center w-20">
                              Status
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedMasterPricing.map((item, index) => {
                            const isChanged = hasProviderChanged(item.provider, item.fee, item.default_fee);
                            const editValue = getMasterEditValue(item.provider, item.fee);
                            
                            return (
                              <TableRow key={index} className="border-border">
                                <TableCell className="font-medium">
                                  {item.provider}
                                  {item.has_proxy && (
                                    <span className="ml-2 text-[10px] text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded">
                                      Proxy
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="font-mono text-muted-foreground text-right text-sm">
                                  {item.default_fee?.toFixed(1) || item.fee.toFixed(1)}%
                                </TableCell>
                                <TableCell className="text-right">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={editValue}
                                    onChange={(e) => handleMasterPriceEdit(item.provider, e.target.value)}
                                    className={`w-20 h-8 text-sm text-right bg-background/50 ml-auto font-mono ${isChanged ? 'border-primary' : ''}`}
                                    data-testid={`master-fee-${item.provider}`}
                                  />
                                </TableCell>
                                <TableCell className="text-center">
                                  {isChanged ? (
                                    <Badge variant="outline" className="text-xs border-primary text-primary">
                                      Modified
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">Default</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
                
                <Alert className="border-muted bg-muted/30">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm text-muted-foreground">
                    Master pricing sets the base fees for all providers. Customer-specific adjustments are applied on top of these base prices.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customer Pricing Tab */}
          <TabsContent value="customer" className="space-y-4">
            {/* Customer Selector */}
            <Card className="glass-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="flex-1 max-w-md">
                    <Label className="text-sm text-muted-foreground mb-2 block">Select Customer</Label>
                    <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                      <SelectTrigger className="bg-background/50" data-testid="customer-select">
                        <SelectValue placeholder="Choose a customer profile..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(customer => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button
                      onClick={() => setShowCreateDialog(true)}
                      className="gap-2"
                      data-testid="create-customer-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Create New Customer
                    </Button>
                    {selectedCustomerId && (
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteDialog(true)}
                        className="border-destructive/50 text-destructive hover:bg-destructive/10"
                        data-testid="delete-customer-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedCustomer ? (
              <>
                {/* Bulk Adjustment Panel */}
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-base font-medium">Bulk Adjustment</CardTitle>
                    <CardDescription>
                      Apply bulk fee adjustments for {selectedCustomer.name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Direct Fee Adjustment */}
                    <div className="p-4 rounded-lg bg-background/30 border border-border space-y-4">
                      <h4 className="font-medium text-sm text-foreground flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary"></span>
                        Direct Fee Adjustment
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={adjustmentType} onValueChange={(v) => handleAdjustmentChange('type', v, false)}>
                            <SelectTrigger className="bg-background/50" data-testid="adjustment-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Points (+8 = 12% → 20%)</SelectItem>
                              <SelectItem value="percent">Relative % (+10% of 12% = 13.2%)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Value</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={adjustmentValue}
                            onChange={(e) => handleAdjustmentChange('value', e.target.value, false)}
                            className="bg-background/50"
                            placeholder={adjustmentType === 'flat' ? '-1 or +8' : '-5 or +10'}
                            data-testid="adjustment-value"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Preview</Label>
                          <div className="h-10 flex items-center text-sm text-muted-foreground">
                            {adjustmentValue !== 0 ? (
                              <span>
                                {adjustmentType === 'flat' 
                                  ? `${adjustmentValue > 0 ? '+' : ''}${adjustmentValue} points`
                                  : `${adjustmentValue > 0 ? '+' : ''}${adjustmentValue}% of base`
                                }
                              </span>
                            ) : (
                              <span>No adjustment</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Proxy Fee Adjustment */}
                    <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-4">
                      <h4 className="font-medium text-sm text-foreground flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        Proxy Fee Adjustment (added on top of Direct Fee)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={proxyAdjustmentType} onValueChange={(v) => handleAdjustmentChange('type', v, true)}>
                            <SelectTrigger className="bg-background/50" data-testid="proxy-adjustment-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Points (+8 = 12% → 20%)</SelectItem>
                              <SelectItem value="percent">Relative % (+10% of 12% = 13.2%)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Value</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={proxyAdjustmentValue}
                            onChange={(e) => handleAdjustmentChange('value', e.target.value, true)}
                            className="bg-background/50"
                            placeholder={proxyAdjustmentType === 'flat' ? '-1 or +8' : '-5 or +10'}
                            data-testid="proxy-adjustment-value"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Preview</Label>
                          <div className="h-10 flex items-center text-sm text-muted-foreground">
                            {proxyAdjustmentValue !== 0 ? (
                              <span>
                                {proxyAdjustmentType === 'flat' 
                                  ? `${proxyAdjustmentValue > 0 ? '+' : ''}${proxyAdjustmentValue} points`
                                  : `${proxyAdjustmentValue > 0 ? '+' : ''}${proxyAdjustmentValue}% of base`
                                }
                              </span>
                            ) : (
                              <span>No adjustment</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Rounding & Actions */}
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="space-y-2">
                        <Label>Rounding</Label>
                        <Select value={rounding} onValueChange={(v) => handleAdjustmentChange('rounding', v)}>
                          <SelectTrigger className="w-40 bg-background/50" data-testid="rounding">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="1">1 decimal</SelectItem>
                            <SelectItem value="2">2 decimals</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 ml-auto">
                        <Button
                          variant="outline"
                          onClick={() => fetchCustomerDetails(selectedCustomerId)}
                          disabled={!hasChanges}
                          data-testid="reset-btn"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Reset
                        </Button>
                        <Button
                          onClick={handleSaveCustomerPricing}
                          disabled={!hasChanges || isSaving}
                          className="gap-2"
                          data-testid="save-pricing-btn"
                        >
                          {isSaving ? (
                            <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          Save Changes
                        </Button>
                      </div>
                    </div>

                    {/* Info */}
                    <Alert className="border-muted bg-muted/30">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-sm text-muted-foreground">
                        Fees are capped between 0% and 100%. Override fees take precedence over bulk adjustments.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                {/* Overrides Table */}
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-base font-medium">Provider Overrides</CardTitle>
                    <CardDescription>
                      Set custom fees for specific providers. Override fees take precedence over bulk adjustments.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="relative max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search provider..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="pl-10 bg-background/50"
                        data-testid="customer-search"
                      />
                    </div>

                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 650px)', minHeight: '300px' }}>
                        <Table data-testid="customer-pricing-table">
                          <TableHeader className="sticky top-0 bg-card z-10">
                            <TableRow className="border-border hover:bg-transparent">
                              <TableHead className="font-semibold text-foreground">Provider</TableHead>
                              <TableHead className="font-semibold text-foreground text-center" colSpan={3}>
                                <div className="flex items-center justify-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                                  Direct Fee
                                </div>
                              </TableHead>
                              <TableHead className="font-semibold text-foreground text-center" colSpan={3}>
                                <div className="flex items-center justify-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                  Proxy Fee (on top of Direct)
                                </div>
                              </TableHead>
                            </TableRow>
                            <TableRow className="border-border hover:bg-transparent">
                              <TableHead></TableHead>
                              <TableHead className="text-xs text-muted-foreground text-right">Base</TableHead>
                              <TableHead className="text-xs text-muted-foreground text-right w-20">Override</TableHead>
                              <TableHead className="text-xs text-primary text-right">Effective</TableHead>
                              <TableHead className="text-xs text-muted-foreground text-right">Base (=Direct)</TableHead>
                              <TableHead className="text-xs text-muted-foreground text-right w-20">Override</TableHead>
                              <TableHead className="text-xs text-amber-400 text-right">Effective</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredCustomerPricing.map((item, index) => {
                              const hasProxy = item.has_proxy || proxyProviders.includes(item.provider);
                              // Calculate effective direct fee first
                              const effectiveFee = getEffectiveFee(item.provider, item.fee, false, null);
                              // Calculate proxy fee based on effective direct fee (only for proxy-enabled providers)
                              const effectiveProxyFee = hasProxy ? getEffectiveFee(item.provider, item.fee, true, effectiveFee) : null;
                              const hasOverride = overrides[item.provider] !== undefined && overrides[item.provider] !== '';
                              const hasProxyOverride = proxyOverrides[item.provider] !== undefined && proxyOverrides[item.provider] !== '';

                              return (
                                <TableRow key={index} className="border-border">
                                  <TableCell className="font-medium">
                                    {item.provider}
                                    {hasProxy && (
                                      <span className="ml-2 text-[10px] text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded">
                                        Proxy
                                      </span>
                                    )}
                                  </TableCell>
                                  {/* Direct Fee */}
                                  <TableCell className="font-mono text-muted-foreground text-right text-sm">
                                    {item.fee.toFixed(1)}%
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Input
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      max="100"
                                      value={overrides[item.provider] ?? ''}
                                      onChange={(e) => handleOverrideChange(item.provider, e.target.value, false)}
                                      placeholder="-"
                                      className="w-16 h-7 text-xs text-right bg-background/50 ml-auto"
                                      data-testid={`override-fee-${item.provider}`}
                                    />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <span className={`font-mono font-semibold text-sm ${hasOverride ? 'text-amber-400' : 'text-primary'}`}>
                                        {effectiveFee.toFixed(1)}%
                                      </span>
                                      {hasOverride && (
                                        <span className="text-[10px] text-amber-400">*</span>
                                      )}
                                    </div>
                                  </TableCell>
                                  {/* Proxy Fee - only for proxy-enabled providers */}
                                  <TableCell className="font-mono text-muted-foreground text-right text-sm border-l border-border">
                                    {hasProxy ? `${effectiveFee.toFixed(1)}%` : '-'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {hasProxy ? (
                                      <Input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="100"
                                        value={proxyOverrides[item.provider] ?? ''}
                                        onChange={(e) => handleOverrideChange(item.provider, e.target.value, true)}
                                        placeholder="-"
                                        className="w-16 h-7 text-xs text-right bg-background/50 ml-auto"
                                        data-testid={`override-proxy-${item.provider}`}
                                      />
                                    ) : (
                                      <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {hasProxy ? (
                                      <div className="flex items-center justify-end gap-1">
                                        <span className={`font-mono font-semibold text-sm ${hasProxyOverride ? 'text-amber-400' : 'text-amber-500'}`}>
                                          {effectiveProxyFee.toFixed(1)}%
                                        </span>
                                        {hasProxyOverride && (
                                          <span className="text-[10px] text-amber-400">*</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground text-xs">N/A</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      * indicates an override is applied
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="glass-card">
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    Select a customer profile or create a new one to manage pricing adjustments.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Customer Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Customer Profile</DialogTitle>
              <DialogDescription>
                Create a new customer profile to manage custom pricing adjustments.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="customer-name">Customer Name *</Label>
                <Input
                  id="customer-name"
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  placeholder="e.g., Acme Corporation"
                  data-testid="new-customer-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email">Email (optional)</Label>
                <Input
                  id="customer-email"
                  type="email"
                  value={newCustomerEmail}
                  onChange={(e) => setNewCustomerEmail(e.target.value)}
                  placeholder="contact@acme.com"
                  data-testid="new-customer-email"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCustomer} disabled={isCreating} data-testid="confirm-create-btn">
                {isCreating ? 'Creating...' : 'Create Profile'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Customer Profile</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedCustomer?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteCustomer} data-testid="confirm-delete-btn">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPricing;
